import { useEffect, useState } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/error-boundary";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "./hooks/use-auth";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/ui/footer";
import { ApiKeySettingsDialog } from "@/components/ApiKeySettingsDialog";
import LoginPage from "./pages/login";
import DashboardPage from "./pages/dashboard";
import ProjectCreatePage from "./pages/project-create";
import ProjectEditPage from "./pages/project-edit";
import ProjectSearchPage from "./pages/project-search";
import CompletedProjectsPage from "./pages/completed-projects";
import KnowledgeBasePage from "./pages/knowledge-base";
import PositionsLibraryPage from "./pages/positions-library";
import CalculationTemplatesPage from "./pages/calculation-templates";
import PhotoAnalysisPage from "./pages/photo-analysis";
import HelpPage from "./pages/help";
import ProjectProtokollPage from "./pages/project-protokoll";
import InvoiceEditPage from "./pages/invoice-edit";
import ProjectInvoicesPage from "./pages/project-invoices";
import NotFound from "./pages/not-found";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-bauwerk-orange rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-bauwerk-light-gray">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-bauwerk-orange rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      <Route path="/login">
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      </Route>
      <Route path="/dashboard">
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/project/create">
        <ProtectedRoute>
          <ProjectCreatePage />
        </ProtectedRoute>
      </Route>
      <Route path="/project/edit/:id">
        <ProtectedRoute>
          <ProjectEditPage />
        </ProtectedRoute>
      </Route>
      <Route path="/invoice/:id">
        <ProtectedRoute>
          <InvoiceEditPage />
        </ProtectedRoute>
      </Route>
      <Route path="/project/:id/invoices">
        <ProtectedRoute>
          <ProjectInvoicesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/search">
        <ProtectedRoute>
          <ProjectSearchPage />
        </ProtectedRoute>
      </Route>
      <Route path="/completed">
        <ProtectedRoute>
          <CompletedProjectsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/knowledge-base">
        <ProtectedRoute>
          <KnowledgeBasePage />
        </ProtectedRoute>
      </Route>
      <Route path="/positions-library">
        <ProtectedRoute>
          <PositionsLibraryPage />
        </ProtectedRoute>
      </Route>
      <Route path="/calculation-templates">
        <ProtectedRoute>
          <CalculationTemplatesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/photo-analysis">
        <ProtectedRoute>
          <PhotoAnalysisPage />
        </ProtectedRoute>
      </Route>
      <Route path="/help">
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      </Route>
      <Route path="/project/:id/protokoll">
        <ProtectedRoute>
          <ProjectProtokollPage />
        </ProtectedRoute>
      </Route>
      <Route path="/">
        <Redirect to="/dashboard" />
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function ApiKeyDialogController() {
  const [open, setOpen] = useState(false);
  const [missingService, setMissingService] = useState<"anthropic" | "openai" | undefined>(undefined);

  useEffect(() => {
    const onMissing = (ev: Event) => {
      const detail = (ev as CustomEvent<{ service?: "anthropic" | "openai" }>).detail;
      setMissingService(detail?.service);
      setOpen(true);
    };
    const onOpen = () => { setMissingService(undefined); setOpen(true); };
    window.addEventListener("missing-api-key", onMissing);
    window.addEventListener("open-api-key-settings", onOpen);
    return () => {
      window.removeEventListener("missing-api-key", onMissing);
      window.removeEventListener("open-api-key-settings", onOpen);
    };
  }, []);

  return (
    <ApiKeySettingsDialog
      open={open}
      onOpenChange={(v) => { if (!v) setMissingService(undefined); setOpen(v); }}
      missingService={missingService}
    />
  );
}

function StorageQuotaListener() {
  const { toast } = useToast();
  useEffect(() => {
    const onQuota = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as
        | { projectId?: string; candidates?: Array<{ name: string }> }
        | undefined;
      const candidates = detail?.candidates ?? [];
      const list = candidates.slice(0, 5).map((c) => c.name).join(', ');
      toast({
        title: 'Lokaler Speicher voll',
        description:
          candidates.length > 0
            ? `Server hat deine Daten weiterhin gespeichert. Vorschlag: alte Projekte aus dem lokalen Cache entfernen (${list}${candidates.length > 5 ? ', …' : ''}).`
            : 'Server hat deine Daten weiterhin gespeichert. Bitte alte Projekte aus dem lokalen Cache entfernen.',
        variant: 'destructive',
      });
    };
    window.addEventListener('storage-quota-exceeded', onQuota as EventListener);
    return () => window.removeEventListener('storage-quota-exceeded', onQuota as EventListener);
  }, [toast]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <StorageQuotaListener />
          <ApiKeyDialogController />
          <Router />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
